import { VIZIT_BENCHMARK_NAMES, isVizitRetailer } from '../benchmark-select-field';
import type { PickerProductCategory } from './types';

/** "Pet Supplies > Cats": a category with the ones above it, as the category filters write it. */
export function toProductCategoryPath(category: PickerProductCategory) {
  return [...(category.parents ?? []), category.name].join(' > ');
}

/**
 * "Walmart": the retailer's name as the benchmark picker writes it. A retailer the picker does not know shows as it
 * comes, and a missing one adds nothing.
 */
function toRetailerLabel({ retailer }: PickerProductCategory) {
  if (!retailer) return '';
  return isVizitRetailer(retailer) ? VIZIT_BENCHMARK_NAMES[retailer] : retailer;
}

/** The keys that show more than once, counted in one pass. */
function findRepeated(keys: string[]) {
  const counts = new Map<string, number>();
  for (const key of keys) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return new Set([...counts].filter(([, count]) => count > 1).map(([key]) => key));
}

/**
 * The IDs of the categories that share their name and the path written under it with another listed with them on
 * another retailer: the same category on two retailers, say. Only their retailer tells them apart, so only they show
 * it. Ones that share their retailer too (two nodes of one retailer's tree) are left to the ID.
 */
export function findSharedPathIds(categories: PickerProductCategory[]): Set<string> {
  // The parents as written, so ["Pets > Cats"] matches ["Pets", "Cats"]; the name apart, as one can hold " > " too, and
  // "Pet Supplies > Cats" alone must not match "Cats" under "Pet Supplies".
  const keyOf = (category: PickerProductCategory) =>
    JSON.stringify([category.name, (category.parents ?? []).join(' > ')]);
  const retailersByKey = new Map<string, Set<string>>();
  for (const category of categories) {
    const key = keyOf(category);
    retailersByKey.set(key, (retailersByKey.get(key) ?? new Set()).add(toRetailerLabel(category)));
  }
  return new Set(
    categories.filter((category) => (retailersByKey.get(keyOf(category))?.size ?? 0) > 1).map(({ id }) => id),
  );
}

/**
 * What tells each category apart from the others listed with it, by ID: the categories above it when another shares
 * its name, and its retailer when another shares those too. A name listed once gets none. The picker and the copy
 * about categories use this rule, so a category reads the same in both. A caller that already has
 * `findSharedPathIds(categories)` passes it rather than have it worked out again.
 */
export function getProductCategoryDetails(
  categories: PickerProductCategory[],
  sharedPathIds = findSharedPathIds(categories),
): Map<string, string[]> {
  const repeatedNames = findRepeated(categories.map((category) => category.name));
  return new Map(
    categories.map((category) => [
      category.id,
      [
        repeatedNames.has(category.name) ? (category.parents ?? []).join(' > ') : '',
        sharedPathIds.has(category.id) ? toRetailerLabel(category) : '',
      ].filter(Boolean),
    ]),
  );
}

/**
 * Each category's name as a sentence writes it, by ID: "Cats (Pet Supplies, Amazon)" when other categories listed
 * with it share its name, else just "Cats".
 */
export function toDistinctProductCategoryNames(categories: PickerProductCategory[]): Map<string, string> {
  const details = getProductCategoryDetails(categories);
  return new Map(
    categories.map((category) => {
      const extra = details.get(category.id) ?? [];
      return [category.id, extra.length > 0 ? `${category.name} (${extra.join(', ')})` : category.name];
    }),
  );
}

/**
 * Where a category sits, as every list of categories shows and names it, worked out once:
 * - `path`, written under its name: "Pet Supplies > Cats > Food";
 * - `label`, its accessible name: "Wet Food, Pet Supplies, Cats, Food", then `state` ("scoring") if given.
 *
 * The retailer is left out, as the rest of the platform leaves it out, unless `withRetailer` asks for it: lists ask
 * only for a category that shares its name and parents with another listed (see `findSharedPathIds`), and both then
 * end with it ("Pet Supplies · Walmart"). The label lists each parent on its own, as screen readers read " > " aloud.
 * What is not known is left out: a category without parents gets a `path` of '' and is named by its name alone.
 */
export function describeCategoryPath(
  category: PickerProductCategory,
  { state, withRetailer = false }: { state?: string; withRetailer?: boolean } = {},
) {
  const parents = category.parents ?? [];
  const retailer = withRetailer ? toRetailerLabel(category) : '';
  return {
    path: [parents.join(' > '), retailer].filter(Boolean).join(' · '),
    label: [category.name, ...parents, retailer, state].filter(Boolean).join(', '),
  };
}

/**
 * The IDs of the categories that read the same as another listed with them, to the eye (one name and one path: two
 * regions of one retailer, say) or to a screen reader (one accessible name: "Tools, Parts" named so, or "Tools" under
 * "Parts"). Lists add "ID <id>" to those rows, so which one is picked is never a guess.
 */
export function findIndistinctCategoryIds(
  rows: { category: PickerProductCategory; path: string; label: string }[],
): Set<string> {
  const shown = rows.map(({ category, path }) => `${category.name}\n${path}`);
  const repeatedShown = findRepeated(shown);
  const repeatedLabels = findRepeated(rows.map(({ label }) => label));
  return new Set(
    rows
      .filter(({ label }, index) => repeatedShown.has(shown[index] as string) || repeatedLabels.has(label))
      .map(({ category }) => category.id),
  );
}

const categoryCollator = new Intl.Collator(undefined, { numeric: true });

/**
 * Categories in the picker's order: by name, then parents and retailer, which tell apart categories that share a
 * name, then ID. Scorings come most recently selected first, so listing them as they come would move a category to
 * the top each time it is picked.
 */
export function sortPickerCategories(categories: PickerProductCategory[]): PickerProductCategory[] {
  return [...categories].sort(
    (a, b) =>
      categoryCollator.compare(a.name, b.name) ||
      categoryCollator.compare(a.parents?.join(' / ') ?? '', b.parents?.join(' / ') ?? '') ||
      categoryCollator.compare(a.retailer ?? '', b.retailer ?? '') ||
      categoryCollator.compare(a.id, b.id),
  );
}
